import { useState, useEffect } from 'react';
import { DEFAULT_ACCESS_CONFIG, STORAGE_KEY } from '../constants';
import { RoleAccessConfig } from '../types';
import { useRemoteRoleAccess } from '@/hooks/useRemoteRoleAccess';

export function useRoleAccessConfig() {
  const { data: remoteConfig, isLoading } = useRemoteRoleAccess();
  const [accessConfig, setAccessConfig] = useState<RoleAccessConfig>(DEFAULT_ACCESS_CONFIG);

  // Sync with remote config when it loads
  useEffect(() => {
    if (remoteConfig) {
      setAccessConfig(remoteConfig);
    }
  }, [remoteConfig]);

  // Handle checkbox change
  const handleAccessChange = (role: string, pageId: string, hasAccess: boolean) => {
    setAccessConfig(prev => {
      const updatedConfig = { ...prev };

      if (hasAccess) {
        // Add page to role's access list
        updatedConfig[role] = [...(updatedConfig[role] || []), pageId];
      } else {
        // Remove page from role's access list
        updatedConfig[role] = (updatedConfig[role] || []).filter(id => id !== pageId);
      }

      return updatedConfig;
    });
  };

  // Ensure dashboard access for all roles
  useEffect(() => {
    if (isLoading) return;

    setAccessConfig(prev => {
      const updatedConfig = { ...prev };
      const rolesWithDashboard = ['admin', 'finance', 'warehouse', 'seller', 'benchmark', 'coordinator', 'quality'];

      let changed = false;
      rolesWithDashboard.forEach(role => {
        if (!updatedConfig[role]) {
          updatedConfig[role] = ['dashboard'];
          changed = true;
        } else if (!updatedConfig[role].includes('dashboard')) {
          updatedConfig[role] = [...updatedConfig[role], 'dashboard'];
          changed = true;
        }
      });

      return changed ? updatedConfig : prev;
    });
  }, [isLoading]);

  return {
    accessConfig,
    setAccessConfig,
    handleAccessChange,
    isLoading
  };
}
